import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { XIcon } from '@heroicons/react/outline';
import { PERMISSION_GROUPS } from '../constants/permissions';

export default function RoleFormModal({ role, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    priority: 50,
    isActive: true,
    permissionIds: []
  });
  const [permissions, setPermissions] = useState([]);
  const [permissionsByResource, setPermissionsByResource] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchPermissions();
    if (role) {
      setFormData({
        name: role.name || '',
        displayName: role.displayName || '',
        description: role.description || '',
        priority: role.priority || 50,
        isActive: role.isActive !== undefined ? role.isActive : true,
        permissionIds: role.permissions?.map(p => p.id) || []
      });
    }
  }, [role]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/permissions/grouped', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allPerms = [];
      const grouped = {};
      
      Object.keys(response.data).forEach(resource => {
        grouped[resource] = response.data[resource];
        allPerms.push(...response.data[resource]);
      });
      
      setPermissions(allPerms);
      setPermissionsByResource(grouped);
      
      // Expand all groups by default
      const expanded = {};
      Object.keys(grouped).forEach(key => {
        expanded[key] = true;
      });
      setExpandedGroups(expanded);
      
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => {
      const newIds = prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId];
      return { ...prev, permissionIds: newIds };
    });
  };

  const handleSelectAllInGroup = (resource) => {
    const groupPermissions = permissionsByResource[resource] || [];
    const groupIds = groupPermissions.map(p => p.id);
    const allSelected = groupIds.every(id => formData.permissionIds.includes(id));
    
    setFormData(prev => {
      let newIds;
      if (allSelected) {
        // Deselect all in group
        newIds = prev.permissionIds.filter(id => !groupIds.includes(id));
      } else {
        // Select all in group
        newIds = [...new Set([...prev.permissionIds, ...groupIds])];
      }
      return { ...prev, permissionIds: newIds };
    });
  };

  const handleSelectAll = () => {
    if (formData.permissionIds.length === permissions.length) {
      // Deselect all
      setFormData(prev => ({ ...prev, permissionIds: [] }));
    } else {
      // Select all
      setFormData(prev => ({ ...prev, permissionIds: permissions.map(p => p.id) }));
    }
  };

  const toggleGroup = (resource) => {
    setExpandedGroups(prev => ({
      ...prev,
      [resource]: !prev[resource]
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Role name must be lowercase letters, numbers, and underscores only';
    }
    
    if (formData.priority < 1 || formData.priority > 100) {
      newErrors.priority = 'Priority must be between 1 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = role
        ? `/api/roles/${role.id}`
        : '/api/roles';
      
      const method = role ? 'put' : 'post';
      
      await api[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Role ${role ? 'updated' : 'created'} successfully`);
      onClose(true); // Close and refresh
    } catch (error) {
      console.error('Error saving role:', error);
      const message = error.response?.data?.message || `Failed to ${role ? 'update' : 'create'} role`;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getResourceDisplayName = (resource) => {
    return resource
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getPermissionDisplayName = (permission) => {
    return permission.displayName || permission.name.split('.').pop().replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div className="fixed z-50 inset-0 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => onClose(false)}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {role ? 'Edit Role' : 'Create New Role'}
                </h3>
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Information */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      disabled={role?.isSystem}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.displayName
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                      } ${role?.isSystem ? 'bg-gray-100' : ''}`}
                      placeholder="e.g., Account Manager"
                    />
                    {errors.displayName && (
                      <p className="mt-1 text-sm text-red-600">{errors.displayName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Role Name (System) *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={role?.isSystem}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.name
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                      } ${role?.isSystem ? 'bg-gray-100' : ''}`}
                      placeholder="e.g., account_manager"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Lowercase, no spaces</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Describe what this role is for..."
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Priority (1-100)
                    </label>
                    <input
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      min="1"
                      max="100"
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.priority
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                      }`}
                    />
                    {errors.priority && (
                      <p className="mt-1 text-sm text-red-600">{errors.priority}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Lower number = higher priority</p>
                  </div>

                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Permissions Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-gray-900">
                    Permissions ({formData.permissionIds.length} selected)
                  </h4>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {formData.permissionIds.length === permissions.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="space-y-3">
                  {Object.keys(permissionsByResource).map(resource => {
                    const groupPerms = permissionsByResource[resource];
                    const allSelected = groupPerms.every(p => formData.permissionIds.includes(p.id));
                    const someSelected = groupPerms.some(p => formData.permissionIds.includes(p.id));
                    const isExpanded = expandedGroups[resource];

                    return (
                      <div key={resource} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div
                          className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleGroup(resource)}
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectAllInGroup(resource);
                              }}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-900">
                              {getResourceDisplayName(resource)}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({groupPerms.filter(p => formData.permissionIds.includes(p.id)).length}/{groupPerms.length})
                            </span>
                          </div>
                          <svg
                            className={`h-5 w-5 text-gray-400 transform transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>

                        {isExpanded && (
                          <div className="bg-white px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {groupPerms.map(permission => (
                              <div key={permission.id} className="flex items-start">
                                <input
                                  type="checkbox"
                                  id={`perm-${permission.id}`}
                                  checked={formData.permissionIds.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label
                                  htmlFor={`perm-${permission.id}`}
                                  className="ml-2 text-sm cursor-pointer"
                                >
                                  <span className="text-gray-900">{getPermissionDisplayName(permission)}</span>
                                  {permission.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{permission.description}</p>
                                  )}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : (role ? 'Update Role' : 'Create Role')}
              </button>
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

